import { create, all } from 'mathjs';
import moment from 'moment';

import { CurrencyTypeEnum } from '../model/currency_type.enum';
import { IntervalEnum } from '../model/interval.enum';
import { RangeEnum } from '../model/range.enum';

import TOKEN_NAMES from './token_names';

type CSVData = (string | number)[][];

export interface DataPoint {
  time: number;
  [key: string]: any;
}

const currencyFormatter = (decimals?: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals ?? 0,
  });
};

export default class Utils {
  static getInitDateByRange(endDate: Date, range: RangeEnum): Date {
    const initDate = new Date(endDate.getTime());
    initDate.setSeconds(0);
    initDate.setMilliseconds(0);
    switch (range) {
      case RangeEnum.H8:
        initDate.setHours(endDate.getHours() - 8);
        break;
      case RangeEnum.D1:
        initDate.setDate(endDate.getDate() - 1);
        break;
      case RangeEnum.D5:
        initDate.setDate(endDate.getDate() - 5);
        break;
      case RangeEnum.M1:
        initDate.setMonth(endDate.getMonth() - 1);
        break;
      case RangeEnum.M3:
        initDate.setMonth(endDate.getMonth() - 3);
        break;
      case RangeEnum.M6:
        initDate.setMonth(endDate.getMonth() - 6);
        break;
      case RangeEnum.Y1:
        initDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case RangeEnum.Y2:
        initDate.setFullYear(endDate.getFullYear() - 2);
        break;
      case RangeEnum.ALL:
        initDate.setFullYear(2019);
        initDate.setMonth(2);
        initDate.setDate(30);
        break;
      default:
        break;
    }
    return initDate;
  }

  static getEndDateDateByRange(endDate: Date, range: RangeEnum): Date {
    const initDate = new Date(endDate.getTime());
    initDate.setSeconds(0);
    initDate.setMilliseconds(0);
    switch (range) {
      case RangeEnum.D1:
        initDate.setDate(endDate.getDate() + 1);
        break;
      case RangeEnum.D5:
        initDate.setDate(endDate.getDate() + 5);
        break;
      case RangeEnum.M1:
        initDate.setMonth(endDate.getMonth() + 1);
        break;
      case RangeEnum.M3:
        initDate.setMonth(endDate.getMonth() + 3);
        break;
      case RangeEnum.M6:
        initDate.setMonth(endDate.getMonth() + 6);
        break;
      case RangeEnum.Y1:
        initDate.setFullYear(endDate.getFullYear() + 1);
        break;
      case RangeEnum.ALL:
        initDate.setFullYear(2019);
        initDate.setMonth(0);
        initDate.setDate(1);
        break;
      default:
        break;
    }
    return initDate;
  }

  static sortTenors(tenorArray: any[]) {
    function parseValue(w: any) {
      const d = parseInt(w.replace(/[^0-9]/g, ''));
      return { w, d, l: w.replace(d, '') };
    }

    const tenorFocus = ['W', 'M', 'Y'];
    return tenorArray?.sort((a, b) => {
      a = parseValue(a.tenor ? a.tenor : a);
      b = parseValue(b.tenor ? b.tenor : b);

      const result = tenorFocus.indexOf(a.l) - tenorFocus.indexOf(b.l);
      return result === 0 ? a.d - b.d : result;
    });
  }

  static getIntervalByRange(range: RangeEnum): IntervalEnum {
    switch (range) {
      case RangeEnum.H8:
        return IntervalEnum.ONE_MINUTE;
      case RangeEnum.D1:
        return IntervalEnum.ONE_MINUTE;
      case RangeEnum.D5:
        return IntervalEnum.FIVE_MINUTES;
      case RangeEnum.M1:
        return IntervalEnum.THIRD_MINUTES;
      case RangeEnum.M3:
        return IntervalEnum.TWO_HOURS;
      case RangeEnum.M6:
        return IntervalEnum.FOUR_HOURS;
      case RangeEnum.Y1:
        return IntervalEnum.DAILY;
      case RangeEnum.ALL:
        return IntervalEnum.MONTHLY;
      case RangeEnum.Y2:
      case RangeEnum.CUSTOM:
      default:
        return IntervalEnum.DAILY;
    }
  }

  static getIntervalByFrequency(frequency: string) {
    switch (frequency) {
      case '1':
        return IntervalEnum.ONE_MINUTE;
      case '5':
        return IntervalEnum.FIVE_MINUTES;
      case '10':
        return IntervalEnum.THIRD_MINUTES;
      case '15':
        return IntervalEnum.FIFTEEN_MINUTES;
      case '30':
        return IntervalEnum.THIRD_MINUTES;
      case '60':
        return IntervalEnum.ONE_HOUR;
      default:
        return IntervalEnum.ONE_MINUTE;
    }
  }

  static getMinutesByFrequency(frequency: string) {
    switch (frequency) {
      case '1':
        return 1;
      case '5':
        return 5;
      case '10':
        return 10;
      case '15':
        return 15;
      case '30':
        return 30;
      case '60':
        return 60;
      default:
        return 1;
    }
  }

  static updatePreviusDatebyInterval(date: Date, interval: IntervalEnum): Date {
    switch (interval) {
      case IntervalEnum.ONE_MINUTE:
        date.setMinutes(date.getMinutes() - 1);
        break;
      case IntervalEnum.THREE_MINUTES:
        date.setMinutes(date.getMinutes() - 2);
        break;
      case IntervalEnum.FIVE_MINUTES:
        date.setMinutes(date.getMinutes() - 5);
        break;
      case IntervalEnum.FIFTEEN_MINUTES:
        date.setMinutes(date.getMinutes() - 15);
        break;
      case IntervalEnum.THIRD_MINUTES:
        date.setMinutes(date.getMinutes() - 30);
        break;
      case IntervalEnum.ONE_HOUR:
        date.setHours(date.getHours() - 1);
        break;
      case IntervalEnum.TWO_HOURS:
        date.setHours(date.getHours() - 2);
        break;
      case IntervalEnum.FOUR_HOURS:
        date.setHours(date.getHours() - 4);
        break;
      case IntervalEnum.THREE_HOURS:
        date.setHours(date.getHours() - 3);
        break;
      case IntervalEnum.SIX_HOURS:
        date.setHours(date.getHours() - 6);
        break;
      case IntervalEnum.DAILY:
        date.setDate(date.getDate() - 1);
        break;
      case IntervalEnum.WEEKLY:
        date.setDate(date.getDate() - 7);
        break;
    }

    return date;
  }

  static valueToCurrency = (value: number, decimals?: number) =>
    currencyFormatter(decimals).format(value);

  static formatNumber = (value: number, decimal?: number) => {
    if (decimal) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      }).format(value);
    }

    return new Intl.NumberFormat('en-US').format(value);
  };

  static updateDateToInterval(date: Date, interval: IntervalEnum): Date {
    const currentMinutes = date.getMinutes();
    let minutes: number = currentMinutes;
    let multiple: number = 0;
    let hour = date.getHours();

    switch (interval) {
      case IntervalEnum.FIVE_MINUTES:
        multiple = Math.round(minutes / 5) * 5;
        minutes = multiple > currentMinutes ? multiple - 5 : multiple;
        break;
      case IntervalEnum.FIFTEEN_MINUTES:
        multiple = Math.round(minutes / 15) * 15;
        minutes = multiple > currentMinutes ? multiple - 15 : multiple;
        break;
      case IntervalEnum.THIRD_MINUTES:
        if (currentMinutes > 30) {
          minutes = 30;
        } else {
          minutes = 0;
        }
        break;
      case IntervalEnum.ONE_HOUR:
      case IntervalEnum.TWO_HOURS:
      case IntervalEnum.EIGHT_HOURS:
      case IntervalEnum.SIX_HOURS:
      case IntervalEnum.FOUR_HOURS:
      case IntervalEnum.THREE_HOURS:
        minutes = 0;
        break;
      case IntervalEnum.DAILY:
      case IntervalEnum.WEEKLY:
        minutes = 0;
        hour = 0;
        break;
    }

    date.setHours(hour);
    date.setMinutes(minutes);

    return date;
  }

  static dateStringToTimestamp(date: string) {
    const split = date.split(' ');
    if (split.length !== 2) return 0;

    const dateStr: any = split[0];
    const timeStr: any = split[1];

    const dateTime = new Date(
      Date.UTC(
        dateStr.split('-')[0],
        dateStr.split('-')[1] - 1,
        dateStr.split('-')[2],
        timeStr.split(':')[0],
        timeStr.split(':')[1],
        timeStr.split(':')[2],
      ),
    );
    return dateTime.getTime() / 1000;
  }

  static getRangeByDates(d1: Date, d2: Date): RangeEnum {
    const diffMinutes = Math.floor(Math.abs(d1.getTime() - d2.getTime()) / 1000 / 60);

    switch (diffMinutes) {
      case 1:
        return RangeEnum.D1;
      case 5:
        return RangeEnum.D5;
      case 30:
        return RangeEnum.M1;
      case 120:
        return RangeEnum.M3;
      case 240:
        return RangeEnum.M6;
      case 480:
        return RangeEnum.Y1;
      case 10080:
        return RangeEnum.ALL;
      default:
        return RangeEnum.CUSTOM;
    }
  }

  static isValidDate(d: Date) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  static formatDecimalNumber = (num: string, precision: number) =>
    parseFloat(num).toFixed(precision);

  static parseDate(stringDate: string): Date | null {
    const inputDate = stringDate.length === 7 ? stringDate : '0' + stringDate;

    const months: { [key: string]: number } = {
      JAN: 0,
      FEB: 1,
      MAR: 2,
      APR: 3,
      MAY: 4,
      JUN: 5,
      JUL: 6,
      AUG: 7,
      SEP: 8,
      OCT: 9,
      NOV: 10,
      DEC: 11,
    };

    const day = parseInt(inputDate.slice(0, 2), 10);
    const monthAbbr = inputDate.slice(2, 5);
    const year = parseInt(inputDate.slice(5), 10);

    if (isNaN(day) || isNaN(year) || months[monthAbbr] === undefined) return null;

    const month = months[monthAbbr];
    const fullYear = 2000 + year;

    return new Date(fullYear, month, day);
  }

  static escapeCsvField = (value: unknown): string => {
    const field = String(value);
    if (/[,"\r\n]/.test(field)) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  static downloadToCsv = (filename: string, data: CSVData, headers?: string[]) => {
    let csvContent = '';
    if (headers) {
      csvContent += headers.map(this.escapeCsvField).join(',') + '\n';
    }

    csvContent += data.map((row) => row.map(this.escapeCsvField).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  static getTokenNameBySign = (sign: any) => {
    return TOKEN_NAMES[sign];
  };

  static getTokenIconPathBySign = (sign: any) => {
    const tokenName = this.getTokenNameBySign(sign);
    const path = `/cripto_icons/${tokenName}.svg`;

    return path;
  };

  static expandableArrReportsForLevel = (data: any, maxLevel: number = 1) => {
    const expandableArr: any[] = [];

    const loopingData = (obj: any, level: number) => {
      if (obj && obj.children && obj.children?.length > 0) {
        if (level < maxLevel) {
          obj.children.forEach((child: any) => loopingData(child, level + 1));
        }
        expandableArr.push(obj.id);
      }
    };

    loopingData(data, 0);

    return expandableArr;
  };

  static formatCurrency = (token: CurrencyTypeEnum) => {
    if (token?.endsWith('_USDC')) {
      return token.slice(0, -5);
    }
    return token;
  };

  static csvToObject = (csvData: string) => {
    if (typeof csvData !== 'string') return;
    const csvLines = csvData.trim().split('\n');

    const headers = csvLines[0].split(',');

    const resultArray = csvLines.slice(1).map((line) => {
      const record: any = {};
      let insideQuotes = false;
      let currentField = '';
      let currentIndex = 0;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          record[headers[currentIndex]] = currentField.trim();
          currentField = '';
          currentIndex++;
        } else {
          currentField += char;
        }
      }

      record[headers[currentIndex]] = currentField.trim();

      return record;
    });

    return resultArray;
  };

  static capitalizeFirstLetter = (string: string) => {
    if (!string) return '';
    if (string && string?.length === 0) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  static naturalSort = (arr: string[]): string[] => {
    return arr.sort((a, b) => {
      const partsA = a.match(/(\d+|\D+)/g) || [];
      const partsB = b.match(/(\d+|\D+)/g) || [];

      for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const partA = partsA[i] || '';
        const partB = partsB[i] || '';

        const isNumA = /^\d+$/.test(partA);
        const isNumB = /^\d+$/.test(partB);

        if (isNumA && isNumB) {
          const diff = parseInt(partA, 10) - parseInt(partB, 10);
          if (diff !== 0) return diff;
        } else {
          const cmp = partA.localeCompare(partB);
          if (cmp !== 0) return cmp;
        }
      }

      return 0;
    });
  };

  static generateCompositeIdWithPrefix = (id: string, currency: string, providerKey?: string) => {
    const isCustomWidget = !!providerKey;

    const prefix = `${providerKey || 'default'}-id-`;

    const compositeId =
      currency && currency.length > 0 && !id.endsWith(`_${currency}`) ? `${id}_${currency}` : id;

    return isCustomWidget ? `${prefix}${compositeId}` : compositeId;
  };

  static truncateLabel = (label: string, maxLength = 20) => {
    if (label.length >= maxLength) {
      return `${label.substring(0, maxLength - 3)}...`;
    }
    return label;
  };

  static generateRandomId = () => Math.floor(Math.random() * 1_000_000_000).toString();

  static formatDateUtc = (date: string, dateFormat?: string) => {
    const DEFAULT_DATE_FORMAT = 'DD MMM YYYY - HH:mm';

    return `${moment(date).format(dateFormat || DEFAULT_DATE_FORMAT)} UTC`;
  };

  static obscuredProps = { 'data-openreplay-obscured': true };

  static truncateToTwoDecimals(value: string | number): string {
    if (typeof value === 'string') {
      const [integerPart, decimalPart] = value.split('.');
      if (decimalPart) {
        return decimalPart.length > 2 ? `${integerPart}.${decimalPart.slice(0, 2)}` : value;
      }
      return value;
    }

    return this.truncateToTwoDecimals(value.toString());
  }

  static roundPrecision(value: number, decimals = 2) {
    const config = {};
    const math = create(all, config);

    return math.round(value || 0, decimals);
  }

  static parseDataPoints(raw: any[]): DataPoint[] {
    if (!Array.isArray(raw) || raw.length === 0) {
      return [];
    }
    return raw
      .map((item) => {
        if (Array.isArray(item)) {
          const [t, a, b, c, d, e] = item;
          const time = typeof t === 'string' ? Math.floor(new Date(t).getTime() / 1000) : t;
          const pt: DataPoint = { time };
          if (item.length === 6) {
            pt.open = a;
            pt.high = b;
            pt.low = c;
            pt.close = d;
            pt.value = e;
          } else if (item.length === 2) {
            pt.value = a;
          }
          return pt;
        }
        if (item && typeof item === 'object' && item.time != null) {
          return { ...item };
        }
        return null;
      })
      .filter((p): p is DataPoint => p !== null);
  }
}
